import { supabase } from '@/integrations/supabase/client';
import { toFriendlyLimitError } from '@/lib/limitErrors';
import type { DeliverableAggregate, ProcessDeliverableUrlResult } from '../types/deliverable.types';

export class DeliverableRepository {
  /**
   * Fetch campaign deliverable aggregates via get_deliverable_aggregates RPC.
   * Scoped by tenant organization.
   */
  async getCampaignDeliverables(
    orgId: string,
    campaignId: string
  ): Promise<DeliverableAggregate[]> {
    if (!campaignId || !orgId) return [];

    await supabase.rpc('set_current_organization', { org_id: orgId });

    const { data, error } = await supabase.rpc('get_deliverable_aggregates', {
      p_campaign_id: campaignId,
    });

    if (error) throw error;
    return (data || []) as unknown as DeliverableAggregate[];
  }

  /**
   * Import a single deliverable URL by running it through the scrape-post-metrics
   * Edge Function, automatically resolving the influencer, adding missing creators to
   * the CRM and campaign, and saving the reel deliverable.
   */
  async importDeliverableUrl(
    campaignId: string,
    postUrl: string
  ): Promise<ProcessDeliverableUrlResult> {
    const { data, error } = await supabase.functions.invoke<ProcessDeliverableUrlResult>(
      'scrape-post-metrics',
      {
        body: {
          campaign_id: campaignId,
          post_url: postUrl,
        },
      }
    );

    if (error) {
      let errorDetail = error.message;
      let errorCode: string | undefined = undefined;
      try {
        if ('context' in error && error.context) {
          const res = error.context as Response;
          const errorBody = await res.json();
          errorDetail = errorBody.details || errorBody.error || errorBody.message || error.message;
          errorCode = errorBody.code;
        }
      } catch {
        // ignore JSON parse error
      }
      return {
        status: 'failed',
        url: postUrl,
        error: toFriendlyLimitError(errorDetail, errorDetail || 'Failed to process post URL'),
        code: errorCode || (errorDetail?.includes('limit_exceeded') ? 'limit_exceeded' : undefined),
      };
    }

    if (!data) {
      return {
        status: 'failed',
        url: postUrl,
        error: 'No response received from scraping service',
      };
    }

    if (data.status === 'failed' && data.error) {
      return {
        ...data,
        error: toFriendlyLimitError(data.error, data.error),
      };
    }

    return data;
  }
}

export const deliverableRepository = new DeliverableRepository();
